"use client";

import type {TFunction} from "i18next";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import {combinedApprovalStatus, resolveToolApprovalStatus} from "@repo/ui/gailgpt/tool-approval-status";
import {useToolApprovalDecisionStates} from "@/contexts/chat/ToolApprovalDecisionContext";
import {useMutationProposalOutcomes} from "@/contexts/chat/MutationProposalOutcomesContext";
import {ReviewConversationContext, useMutationProposalReview, type MutationProposalReview} from "@/contexts/chat/MutationProposalReviewContext";
import {editsProposalTarget, mutationProposalEditorPaths, mutationProposalHref, mutationProposalReviewParams, reviewEditorPage, type MutationProposal} from "@/services/wingman/mutation-proposal";
import {UnsavedDataContext} from "@/hooks/useHasUnsavedData";
import {useContext, useEffect, useRef, useState} from "react";
import {displayToolPresentation, knownToolActivity} from "@repo/ui/gailgpt/tool-display";
import {presentationText} from "@repo/ui/gailgpt/tool-presentation";
import {approvalPresentation} from "./approvalPresentation";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {Button} from "@/components/ui/button";
import ApprovalReviewSummary, {reviewSummary} from "./ApprovalReviewSummary";
import type {ApprovalBatch, ApprovalPart, ApprovalDecisionOutcome} from "@/types/chatMessages";
import {isApprovalActive} from "@/utils/chat/approvalParts";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import {useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import type {ToolApprovalResponse} from "@/services/wingman";
import ApprovalBatchActions from "./ApprovalBatchActions";
import ApprovalReviewScrollArea from "./ApprovalReviewScrollArea";
import MutationProposalReviewPanel from "./MutationProposalReviewPanel";
import ToolApprovalCard from "./ToolApprovalCard";
import UseCaseApprovalBatch from "./UseCaseApprovalBatch";

/**
 * The Action editor already showing the target hosts its review in place.
 * Keeping the current path and query, whatever its ID's case, keeps the page and its draft mounted.
 */
function inPlaceReviewHref(pathname: string | null, search: string | undefined, proposal: MutationProposal, conversationId: string): string | undefined {
   const page = reviewEditorPage(pathname);
   if (!pathname || !page || !editsProposalTarget(page, proposal)) return;
   const query = new URLSearchParams(search);
   mutationProposalReviewParams(proposal, conversationId).forEach((value, key) => query.set(key, value));
   return `${pathname}?${query}`;
}

/** This proposal is the one the current page is reviewing. */
function proposalMatchesPageReview(proposal: MutationProposal, pageReview: MutationProposalReview, conversationId?: string): boolean {
   return proposal.proposalId === pageReview.proposalId && conversationId === pageReview.conversationId && editsProposalTarget(pageReview, proposal);
}

function proposalReviewHrefs(proposal: MutationProposal | undefined, conversationId: string | undefined, pathname: string | null, search: string | undefined) {
   const canonicalHref = proposal?.capability === "use_cases_update" ? mutationProposalHref(proposal, conversationId) : undefined;
   const inPlaceHref = proposal && canonicalHref ? inPlaceReviewHref(pathname, search, proposal, conversationId!) : undefined;
   return {canonicalHref, inPlaceHref};
}

function approvalReviewTitle(parts: ApprovalPart[], t: TFunction<"ask">): string {
   if (parts.length !== 1) return t("toolApproval.proposals", {count: parts.length});
   const call = parts[0].data;
   if (call.approval.proposal) return call.approval.proposal.summary;
   const {presentation} = approvalPresentation(call);
   if (!call.approval.previewUnavailable && presentation) {
      return presentationText(displayToolPresentation(presentation, call.parameters, t).title, t);
   }
   return knownToolActivity(call.functionName, call.parameters, t)?.inProgressText ?? call.functionName;
}

function isUseCaseBatch(parts: ApprovalPart[], batch: ApprovalReviewProps["batch"]): boolean {
   return parts.length > 1 && parts.every(part => part.data.functionName === "use_cases_create" &&
      !part.data.approval.proposal && !approvalPresentation(part.data).unavailable) &&
      (!batch || batch.pending.every(part => parts.some(item => item.data.approval.id === part.data.approval.id)));
}

function inlineReviewParts(parts: ApprovalPart[], statuses: ReturnType<typeof resolveToolApprovalStatus>[], options: {
   panelAvailable: boolean;
   inlineScriptReview: boolean;
   matchingPart?: ApprovalPart;
   isOpen: boolean;
}) {
   if ((options.panelAvailable && !options.inlineScriptReview) || options.matchingPart || options.isOpen) return [];
   return parts.filter((part, index) => part.data.approval.proposal?.capability === "phone_scripts_update" ||
      (statuses[index] !== "completed" && statuses[index] !== "denied"));
}

interface ApprovalReviewProps {
   parts: ApprovalPart[];
   reviewParts?: ApprovalPart[];
   conversationId?: string;
   messageId?: string;
   approvalMessageIds?: Record<string, string>;
   activeMessageId?: string;
   disabled: boolean;
   onRespond?: (decisions: ToolApprovalResponse[]) => Promise<ApprovalDecisionOutcome>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
   batch?: ApprovalBatch & {inline: boolean};
}

/** The chat owns live approval state; the shared panel only hosts its review UI. */
export default function ApprovalReview({parts, reviewParts = parts, conversationId, messageId, approvalMessageIds, activeMessageId, disabled, onRespond, onRequestFresh, batch}: ApprovalReviewProps) {
   const {t} = useTranslation("ask");
   const router = useRouter();
   const dock = useGailDock();
   const pathname = usePathname();
   const search = useSearchParams();
   const pageReview = useMutationProposalReview();
   const reportOutcome = useMutationProposalOutcomes()?.report;
   const reviewConversation = useContext(ReviewConversationContext);
   const {checkBeforeAction} = useContext(UnsavedDataContext);
   const {available: panelAvailable, activeArtifact, openArtifact, closeArtifact, approvalPanelHost, autoOpenedIds, coversConversation} = useArtifactPanel();
   const busy = useRef(false);
   const [pendingApprovalIds, setPendingApprovalIds] = useState<string[]>([]);
   const decisions = useToolApprovalDecisionStates(parts.map(part => part.data.approval.id));
   const pending = pendingApprovalIds.length > 0 || decisions.some(state => state.submitting) || !!batch?.submitting;
   const id = `approval:${parts[0].data.approval.id}`;
   const isOpen = activeArtifact?.kind === "approval" && activeArtifact.id === id;
   const title = approvalReviewTitle(parts, t);
   const isActive = (part: ApprovalPart) => isApprovalActive(part, {messageId, approvalMessageIds, activeMessageId});
   // A decision in flight is reported as submitting, so the page holds saves before the server answers.
   const statuses = parts.map((part, index) => resolveToolApprovalStatus({approval: part.data.approval,
      completed: part.isCompleted, active: isActive(part), result: part.result, pending: pendingApprovalIds.includes(part.data.approval.id) || decisions[index].submitting}));
   const status = combinedApprovalStatus(statuses, pending);
   const queued = parts.length === 1 && status === "required" && isActive(parts[0]) ? decisions[0].draft : null;

   const editorPart = parts.find(part => {
      const proposal = part.data.approval?.proposal;
      return !!proposal && !!mutationProposalHref(proposal, conversationId);
   });
   const preview = editorPart?.data.approval?.proposal;
   const summary = reviewSummary(parts, title, status);
   const {objectTitle} = summary;
   const {canonicalHref, inPlaceHref} = proposalReviewHrefs(preview, conversationId, pathname, search?.toString());
   const inlineActions = parts.every(part => !part.data.approval.proposal);
   const inlineScriptReview = parts.some(part => part.data.approval.proposal?.capability === "phone_scripts_update");
   const reviewInPanel = !inlineActions && panelAvailable && !inPlaceHref && !pageReview;
   const matchingPart = parts.find(part => {
      const proposal = part.data.approval?.proposal;
      return pageReview != null && proposal != null && proposalMatchesPageReview(proposal, pageReview, conversationId);
   });
   const matchingStatus = matchingPart ? statuses[parts.indexOf(matchingPart)] : undefined;
   const reviewable = reviewParts.flatMap(part => {
      const proposal = part.data.approval.proposal;
      return proposal?.capability === "use_cases_update" && mutationProposalHref(proposal, conversationId) ? [proposal] : [];
   });
   const reviewIndex = reviewable.findIndex(proposal => proposal.proposalId === matchingPart?.data.approval.proposal?.proposalId);
   const navigateToReview = async ({canonicalHref, inPlaceHref}: ReturnType<typeof proposalReviewHrefs>) => {
      const href = inPlaceHref ?? canonicalHref;
      if (!href || (!inPlaceHref && !await checkBeforeAction())) return;
      // On a phone the conversation covers the editor being opened for review.
      reviewConversation?.showPage();
      router.push(href);
   };
   const openProposal = (proposal: MutationProposal) =>
      navigateToReview(proposalReviewHrefs(proposal, conversationId, pathname, search?.toString()));
   const openScript = async (proposal: MutationProposal) => {
      const [canonicalPath] = mutationProposalEditorPaths(proposal);
      if (!canonicalPath) return;
      const page = reviewEditorPage(pathname);
      const inPlace = !!page && editsProposalTarget(page, proposal);
      if (!inPlace && !await checkBeforeAction()) return;
      const query = new URLSearchParams(inPlace ? search?.toString() : undefined);
      query.delete("proposalId");
      query.delete("conversationId");
      // Keep the existing editor revision and chat session when they are already open.
      if (conversationId && (!dock.isOpen || dock.conversationId !== conversationId)) dock.showConversationOnRight(conversationId);
      if (window.innerWidth < 768) dock.closeDock();
      const path = inPlace ? pathname : canonicalPath;
      const suffix = query.size ? `?${query}` : "";
      router.push(`${path}${suffix}`);
   };
   const setHasReview = pageReview?.setHasReview;
   useEffect(() => {
      if (!matchingPart || !setHasReview) return;
      setHasReview(true);
      return () => setHasReview(false);
   }, [matchingPart, setHasReview]);
   // Approved is still awaiting execution, so only a settled outcome releases the editor.
   const decisionPending = matchingStatus === "required" || matchingStatus === "submitting" || matchingStatus === "approved";
   const setDecisionPending = pageReview?.setDecisionPending;
   useEffect(() => {
      if (!matchingPart || !setDecisionPending) return;
      setDecisionPending(decisionPending);
      return () => setDecisionPending(true);
   }, [matchingPart, setDecisionPending, decisionPending]);
   // Editors stay mounted through a decision and may outlive this conversation, so the page
   // tracks every outcome: it holds their saves during execution and refreshes them after.
   useEffect(() => {
      if (!reportOutcome) return;
      parts.forEach((part, index) => {
         const {id: approvalId, proposal} = part.data.approval;
         if (proposal && mutationProposalHref(proposal, conversationId)) reportOutcome(approvalId, proposal, statuses[index]);
      });
   });
   // A route change must not leave an orphaned proposal in the shared panel.
   useEffect(() => () => closeArtifact(id), [closeArtifact, id]);

   // Additional proposals can join this reply while its review is already open.
   useEffect(() => {
      if (isOpen && activeArtifact?.kind === "approval" && activeArtifact.title !== objectTitle) {
         openArtifact({...activeArtifact, title: objectTitle});
      }
   }, [isOpen, activeArtifact, objectTitle, openArtifact]);

   // Track individual approvals because new proposals can join an existing reply.
   // Remember dismissal across message regrouping and subsequent decision updates.
   const autoOpenIds = JSON.stringify(parts.flatMap((part, index) =>
      isActive(part) && ["required", "approved", "completed"].includes(statuses[index])
         ? [`approval:${part.data.approval.id}`] : []));
   const autoOpen = reviewInPanel && !inlineScriptReview && !!onRespond && !disabled;
   useEffect(() => {
      if (!autoOpen || coversConversation) return;
      const ids: string[] = JSON.parse(autoOpenIds);
      if (ids.every(approvalId => autoOpenedIds.has(approvalId))) return;
      // Wait until mount effects settle so Strict Mode's cleanup cannot close
      // the panel immediately after marking these proposals as already opened.
      let canceled = false;
      queueMicrotask(() => {
         if (canceled || ids.every(approvalId => autoOpenedIds.has(approvalId))) return;
         ids.forEach(approvalId => autoOpenedIds.add(approvalId));
         openArtifact({kind: "approval", id, title: objectTitle});
      });
      return () => { canceled = true; };
   }, [autoOpen, autoOpenIds, coversConversation, autoOpenedIds, id, openArtifact, objectTitle]);

   const respondToDecisions = async (decisions: ToolApprovalResponse[]): Promise<ApprovalDecisionOutcome> => {
      if (busy.current || disabled || !onRespond) return "ignored";
      busy.current = true;
      setPendingApprovalIds(decisions.map(decision => decision.id));
      // The deciding surface reports a failed submission; the proposal stays open to retry.
      try {
         return await onRespond(decisions);
      } finally {
         busy.current = false;
         setPendingApprovalIds([]);
      }
   };

   const respond = (decision: ToolApprovalResponse) => respondToDecisions([decision]);
   const positionFor = (part: ApprovalPart) => {
      const index = batch?.pending.findIndex(item => item.data.approval.id === part.data.approval.id) ?? -1;
      return index >= 0 && batch ? {index: index + 1, total: batch.pending.length, remaining: batch.undecidedIds.length} : undefined;
   };
   const openReview = async () => {
      if (reviewInPanel) { parts.forEach(part => autoOpenedIds.add(`approval:${part.data.approval.id}`)); openArtifact({kind: "approval", id, title: objectTitle}); }
      else if (inPlaceHref || canonicalHref) await navigateToReview({canonicalHref, inPlaceHref});
      else openArtifact({kind: "approval", id, title});
   };
   const renderCard = (part: ApprovalPart, layout: {embedded?: boolean; panel?: boolean; collapseOnApproval?: boolean} = {}) => <ToolApprovalCard
      key={part.data.approval.id} {...layout} position={positionFor(part)}
      compactScript={!layout.panel && !layout.collapseOnApproval && part.data.approval.proposal?.capability === "phone_scripts_update"}
      onOpenScript={part.data.approval.proposal?.capability === "phone_scripts_update" && mutationProposalEditorPaths(part.data.approval.proposal).length
         ? () => openScript(part.data.approval.proposal!) : undefined}
      call={part.data} completed={part.isCompleted} result={part.result} active={isActive(part)}
      disabled={disabled || pending} onRespond={onRespond ? respond : undefined} onRequestFresh={onRequestFresh} />;
   const batchActions = batch && onRespond && <ApprovalBatchActions {...batch} disabled={disabled || pending} onRespond={respondToDecisions} />;
   if (isUseCaseBatch(parts, batch)) return <UseCaseApprovalBatch parts={parts} statuses={statuses} disabled={disabled || pending} onRespond={onRespond ? respondToDecisions : undefined} />;
   // Routine actions are immediately reviewable and actionable in the conversation.
   // Script edits also show their frozen comparison inline, with an optional larger review.
   if (inlineActions) return <>
      {batchActions && <div className="my-3">{batchActions}</div>}
      {parts.map(part => renderCard(part, {collapseOnApproval: parts.length > 1}))}
   </>;

   const inlineParts = inlineReviewParts(parts, statuses, {panelAvailable, inlineScriptReview, matchingPart, isOpen});

   return <>
      {batch?.inline && batchActions && <div className="my-3">{batchActions}</div>}
      {inlineScriptReview && inlineParts.length === parts.length ? <>
         {inlineParts.map(part => renderCard(part))}
         {panelAvailable && <Button variant="ghost" size="sm" className="text-800" onClick={openReview}>{t("toolApproval.review")}</Button>}
      </> : <ApprovalReviewSummary summary={summary} queued={queued} status={status} current={!!matchingPart} isOpen={isOpen} showFields={!inlineParts.length} onOpen={openReview}>
         {inlineParts.map(part => renderCard(part, {embedded: true}))}
      </ApprovalReviewSummary>}
      {matchingPart && pageReview?.host && createPortal(
         <MutationProposalReviewPanel key={matchingPart.data.approval.proposal?.proposalId} call={matchingPart.data} completed={matchingPart.isCompleted} result={matchingPart.result}
            navigation={reviewNavigation(reviewIndex, reviewable, openProposal)}
            position={positionFor(matchingPart)}
            active={isActive(matchingPart)} disabled={disabled || pending} onRespond={onRespond ? respond : undefined} onRequestFresh={onRequestFresh}
            onClose={pageReview.finish} />,
         pageReview.host)}
      {isOpen && approvalPanelHost && createPortal(
         <div className="flex h-full min-h-0 flex-col">
            {batch && onRespond && <div className="shrink-0 border-b border-neutral px-5 py-4"><ApprovalBatchActions {...batch} compact disabled={disabled || pending} onRespond={respondToDecisions} /></div>}
            <ApprovalReviewScrollArea enabled={parts.length > 1} label={t("toolApproval.title")}
               className={parts.length === 1 ? "min-h-0 flex-1" : "bg-100/40 px-4 py-1"}>
               {parts.map(part => renderCard(part, {panel: parts.length === 1, collapseOnApproval: parts.length > 1}))}
            </ApprovalReviewScrollArea>
         </div>, approvalPanelHost)}
   </>;
}

function reviewNavigation(index: number, proposals: MutationProposal[], openProposal: (proposal: MutationProposal) => Promise<void>) {
   if (index < 0 || proposals.length <= 1) return undefined;
   return {
      index: index + 1, total: proposals.length,
      previous: index > 0 ? () => void openProposal(proposals[index - 1]) : undefined,
      next: index < proposals.length - 1 ? () => void openProposal(proposals[index + 1]) : undefined,
   };
}
